/*
  SolidForms — Print Reveal
  ---------------------------------------------------------------------------
  The SolidForms emblem draws itself the way the shop's machines actually work:
  a nozzle travels the bed, lays 15 extruded beads bottom-to-top in a boustro-
  phedon toolpath, the filament glows hot and cools behind the head, then the
  camera tilts flat for the reveal.

  Ported by hand from the Claude Design prototype (print-reveal.jsx). That
  version leaned on React + the `animations-v2` SceneStage runtime; a storefront
  can afford neither, so the geometry, toolpath solver, camera keyframes and
  scene timings are reproduced verbatim here in ~0 dependencies. The DOM is
  built once and only mutated per frame.

  Usage:
    const pr = new SFPrintReveal(el, { hot: '#ff7a1a', mode: 'autoplay' });
    pr.play(); pr.seek(0.5); pr.destroy();

  Modes:
    autoplay  play once when scrolled into view
    loop      play, pause, replay forever
    scrub     progress is driven by the element's travel through the viewport
    manual    nothing happens until you call play()/seek()
*/
(function () {
  'use strict';

  /* ---------- logo geometry (measured from the emblem) ---------- */
  var PW = 1000,
    PH = 907,
    BAR_H = 23;

  var RAW = [
    [0.2702, 0.7862, 0.2034], [0.2036, 0.7643, 0.2465], [0.1825, 0.7424, 0.2889],
    [0.1723, 0.3038, 0.3316], [0.1699, 0.3015, 0.3744], [0.1785, 0.3218, 0.4171],
    [0.1966, 0.7150, 0.4598], [0.2247, 0.7690, 0.5026], [0.2882, 0.7972, 0.5453],
    [0.6735, 0.8128, 0.5881], [0.6907, 0.8183, 0.6313], [0.6836, 0.8175, 0.6740],
    [0.2388, 0.8050, 0.7163], [0.2161, 0.7831, 0.7591], [0.1934, 0.7189, 0.8022]
  ];

  var BARS = RAW.map(function (r) {
    return { L: r[0] * PW, R: r[1] * PW, y: r[2] * PH };
  });

  /* ---------- toolpath: bottom row up, alternating direction ---------- */
  var EXTRUDE_SPEED = 700,
    TRAVEL_SPEED = 2600;
  var HOME = { x: BARS[14].L - 150, y: BARS[14].y + 70 };

  var SEQ = (function () {
    var items = [];
    var t = 0;
    var cur = { x: HOME.x, y: HOME.y };
    for (var k = 0; k < BARS.length; k++) {
      var b = BARS[BARS.length - 1 - k];
      var fwd = k % 2 === 0;
      var sx = fwd ? b.L : b.R;
      var ex = fwd ? b.R : b.L;
      var t0 = t + Math.hypot(sx - cur.x, b.y - cur.y) / TRAVEL_SPEED;
      var t1 = t0 + Math.abs(ex - sx) / EXTRUDE_SPEED;
      items.push({ n: k + 1, y: b.y, sx: sx, ex: ex, fwd: fwd, t0: t0, t1: t1, tv: t });
      cur = { x: ex, y: b.y };
      t = t1;
    }
    return { items: items, total: t };
  })();

  var COOL = 1.15,
    HOT_LEN = 260;

  /* ---------- three motion helpers, nothing else eases ---------- */
  var MOTION = {
    ease: function (p) { return p < 0.5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2; },
    out: function (p) { return 1 - Math.pow(1 - p, 3); },
    lin: function (p) { return p; },
    // camera-continuity pair: accel ends at full speed, decel starts at it
    accel: function (p) { return p * p; },
    decel: function (p) { return 1 - (1 - p) * (1 - p); }
  };

  function lerp(a, b, p) { return a + (b - a) * p; }
  function clamp01(v) { return v < 0 ? 0 : v > 1 ? 1 : v; }

  function mixCam(a, b, p) {
    return {
      tilt: lerp(a.tilt, b.tilt, p), yaw: lerp(a.yaw, b.yaw, p), s: lerp(a.s, b.s, p),
      ty: lerp(a.ty, b.ty, p), grid: lerp(a.grid, b.grid, p), hud: lerp(a.hud, b.hud, p)
    };
  }

  var CAM = {
    c0: { tilt: 69, yaw: -26, s: 0.80, ty: 130, grid: 0, hud: 0 },
    c1: { tilt: 66, yaw: -21, s: 0.87, ty: 108, grid: 1, hud: 1 },
    c2: { tilt: 51, yaw: 11, s: 1.02, ty: 62, grid: 1, hud: 1 },
    c3: { tilt: 29, yaw: 4, s: 1.22, ty: 24, grid: 0.55, hud: 0.7 },
    c4: { tilt: 0, yaw: 0, s: 1.56, ty: 0, grid: 0, hud: 0 }
  };

  /* ---------- scene list (durations in seconds) ---------- */
  var SCENES = [
    { name: 'bed', dur: 0.6 },
    { name: 'print', dur: 2.4 },
    { name: 'settle', dur: 0.7 },
    { name: 'reveal', dur: 1.4 }
  ];
  var TOTAL = SCENES.reduce(function (s, x) { return s + x.dur; }, 0);

  /* ---------- colour helpers ---------- */
  // The hot-filament gradient needs #rrggbb so it can take an alpha suffix.
  function normalizeHex(input, fallback) {
    var h = String(input || '').trim().replace('#', '');
    if (h.length === 3) h = h.replace(/./g, function (c) { return c + c; });
    return /^[0-9a-fA-F]{6}$/.test(h) ? '#' + h : fallback;
  }

  function el(cls, style) {
    var n = document.createElement('div');
    if (cls) n.className = cls;
    if (style) n.setAttribute('style', style);
    return n;
  }

  /* ======================================================================= */

  function SFPrintReveal(root, options) {
    var o = options || {};
    this.root = root;
    this.opts = {
      hot: normalizeHex(o.hot, '#ff7a1a'),
      grid: o.grid !== false,
      hud: o.hud !== false,
      speed: Number(o.speed) > 0 ? Number(o.speed) : 1,
      mode: o.mode || 'autoplay',
      loopDelay: o.loopDelay == null ? 2.2 : Number(o.loopDelay),
      // How much room around the 1000x907 part the camera gets. Larger = smaller logo.
      padding: Number(o.padding) > 0 ? Number(o.padding) : 1.34,
      onEnd: typeof o.onEnd === 'function' ? o.onEnd : null,
      onFrame: typeof o.onFrame === 'function' ? o.onFrame : null
    };

    this._raf = 0;
    this._clock = 0;          // seconds into the timeline
    this._last = 0;
    this._playing = false;
    this._bloom = -1;         // cached: the white-bead glow only changes late
    this._fit = 1;
    this._destroyed = false;

    this._build();
    this._measure();

    this._onResize = this._measure.bind(this);
    window.addEventListener('resize', this._onResize, { passive: true });

    if (SFPrintReveal.reducedMotion()) {
      // Skip the performance, keep the product: render the finished emblem.
      this.seek(1);
      return;
    }

    if (this.opts.mode === 'scrub') this._initScrub();
    else if (this.opts.mode === 'autoplay' || this.opts.mode === 'loop') this._initAutoplay();
    else this.seek(0);
  }

  SFPrintReveal.reducedMotion = function () {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  };

  /* ---------- DOM, built once ---------- */
  SFPrintReveal.prototype._build = function () {
    var hot = this.opts.hot;
    var r = this.root;
    r.classList.add('sfpr');
    r.innerHTML = '';

    var scene = el('sfpr__scene');
    var stage = el('sfpr__stage');
    stage.style.width = PW + 'px';
    stage.style.height = PH + 'px';
    stage.style.marginLeft = -PW / 2 + 'px';
    stage.style.marginTop = -PH / 2 + 'px';

    /* bed grid ------------------------------------------------------------ */
    var grid = null;
    if (this.opts.grid) {
      grid = el('sfpr__grid');
      grid.style.left = '-320px';
      grid.style.top = '-220px';
      grid.style.width = PW + 640 + 'px';
      grid.style.height = PH + 440 + 'px';
      grid.appendChild(el('sfpr__grid-lines'));
      grid.appendChild(el('sfpr__grid-plate'));
      stage.appendChild(grid);

      var rail = el('sfpr__rail');
      rail.style.left = '-320px';
      rail.style.width = PW + 640 + 'px';
      stage.appendChild(rail);
      this.rail = rail;
    }
    this.grid = grid;

    /* the part: one node per extruded bead -------------------------------- */
    var partHolder = el('sfpr__part');
    this.bars = SEQ.items.map(function (it) {
      var bar = el('sfpr__bar');
      bar.style.top = it.y - BAR_H / 2 + 'px';
      bar.style.height = BAR_H + 'px';

      var shadow = el('sfpr__bar-shadow');
      var base = el('sfpr__bar-base');
      var top = el('sfpr__bar-top');
      var heat = el('sfpr__bar-hot');
      shadow.style.borderRadius = BAR_H + 'px';
      base.style.borderRadius = BAR_H / 2 + 'px';
      top.style.borderRadius = BAR_H / 2 + 'px';
      heat.style.borderRadius = BAR_H / 2 + 'px';

      bar.appendChild(shadow);
      bar.appendChild(base);
      bar.appendChild(top);
      bar.appendChild(heat);
      partHolder.appendChild(bar);
      return { it: it, node: bar, top: top, hot: heat };
    });
    stage.appendChild(partHolder);

    /* nozzle -------------------------------------------------------------- */
    var noz = el('sfpr__nozzle');
    var glow = el('sfpr__noz-glow');
    var tip = el('sfpr__noz-tip');
    var block = el('sfpr__noz-block');
    var body = el('sfpr__noz-body');
    var rib1 = el('sfpr__noz-rib');
    var rib2 = el('sfpr__noz-rib');
    var dot = el('sfpr__noz-dot');
    dot.style.background = hot;
    dot.style.boxShadow = '0 0 16px ' + hot;
    [glow, tip, block, body, rib1, rib2, dot].forEach(function (n) { noz.appendChild(n); });
    stage.appendChild(noz);
    this.nozzle = { node: noz, glow: glow, tip: tip, block: block, body: body, rib1: rib1, rib2: rib2, dot: dot };

    scene.appendChild(stage);
    r.appendChild(scene);

    /* bloom wash ---------------------------------------------------------- */
    this.wash = el('sfpr__wash');
    r.appendChild(this.wash);

    /* HUD — rebuilt in real CSS rather than the prototype's 1920px frame so
       it stays legible on a phone and mirrors itself under dir="rtl". ----- */
    if (this.opts.hud) {
      var hud = el('sfpr__hud');
      hud.setAttribute('aria-hidden', 'true');
      var spec = el('sfpr__hud-spec');
      spec.textContent = 'FDM · 0.2 MM LAYER';
      // One flex row along the bottom: the label and the meter share the width
      // instead of being absolutely placed into each other's lap.
      var bar = el('sfpr__hud-bar');
      var layer = el('sfpr__hud-layer');
      var track = el('sfpr__hud-track');
      var fill = el('sfpr__hud-fill');
      var pct = el('sfpr__hud-pct');
      track.appendChild(fill);
      bar.appendChild(layer);
      bar.appendChild(track);
      bar.appendChild(pct);
      hud.appendChild(spec);
      hud.appendChild(bar);
      r.appendChild(hud);
      this.hud = { node: hud, layer: layer, fill: fill, pct: pct };
    }

    this.scene = scene;
    this.stage = stage;
  };

  /* Fit the 1000x907 part into the container, leaving room for the tilt. */
  SFPrintReveal.prototype._measure = function () {
    var w = this.root.clientWidth || 1;
    var h = this.root.clientHeight || 1;
    var pad = this.opts.padding;
    this._fit = Math.min(w / (PW * pad), h / (PH * pad));
    this.scene.style.perspective = Math.max(1200, Math.min(w, h) * 2.2) + 'px';
    if (this._lastCam) this._applyCam(this._lastCam);
  };

  /* ---------- per-frame rendering ---------- */

  SFPrintReveal.prototype._applyCam = function (cam) {
    this._lastCam = cam;
    this.stage.style.transform =
      'translateY(' + cam.ty * this._fit + 'px) scale(' + cam.s * this._fit +
      ') rotateX(' + cam.tilt + 'deg) rotateZ(' + cam.yaw + 'deg)';
    if (this.grid) {
      var vis = cam.grid > 0.002;
      this.grid.style.opacity = vis ? cam.grid : 0;
      this.grid.style.visibility = vis ? 'visible' : 'hidden';
    }
  };

  SFPrintReveal.prototype._renderPart = function (t, bloom) {
    var hot = this.opts.hot;
    var bloomChanged = Math.abs(bloom - this._bloom) > 0.004;
    if (bloomChanged) this._bloom = bloom;

    for (var k = 0; k < this.bars.length; k++) {
      var b = this.bars[k];
      var it = b.it;

      if (t <= it.t0) {
        if (b.node.style.display !== 'none') b.node.style.display = 'none';
        continue;
      }
      if (b.node.style.display === 'none') b.node.style.display = '';

      var f = clamp01((t - it.t0) / (it.t1 - it.t0));
      var hx = lerp(it.sx, it.ex, f);
      var dl = Math.min(it.sx, hx);
      var dr = Math.max(it.sx, hx);
      var w = Math.max(dr - dl, BAR_H * 0.6);
      var heat = t < it.t1 ? 1 : clamp01(1 - (t - it.t1) / COOL);
      var stop = Math.max(0, 100 * (1 - HOT_LEN / w));

      b.node.style.left = dl + 'px';
      b.node.style.width = w + 'px';

      if (bloomChanged) {
        b.top.style.boxShadow =
          '0 0 ' + (9 + bloom * 12) + 'px rgba(255,255,255,' + (0.16 + bloom * 0.16) + '),' +
          'inset 0 -2px 3px rgba(150,150,165,.5)';
      }

      // A cooled bead is pure white plastic — stop painting its heat layer.
      if (heat <= 0.002) {
        if (b.hot.style.opacity !== '0') { b.hot.style.opacity = '0'; b._live = false; }
        continue;
      }
      b._live = true;
      b.hot.style.opacity = heat;
      b.hot.style.background =
        'linear-gradient(to ' + (it.fwd ? 'right' : 'left') + ',' +
        hot + '00 ' + stop + '%,' + hot + 'ff 100%)';
      b.hot.style.boxShadow = '0 0 ' + 18 * heat + 'px ' + hot + (heat > 0.5 ? '88' : '44');
    }
  };

  SFPrintReveal.prototype._renderNozzle = function (n, cam) {
    var noz = this.nozzle;
    if (!n || n.opacity <= 0.001) {
      noz.node.style.opacity = '0';
      noz.node.style.visibility = 'hidden';
      if (this.rail) this.rail.style.opacity = '0';
      return;
    }
    noz.node.style.visibility = 'visible';
    noz.node.style.opacity = n.opacity;
    noz.node.style.left = n.x + 'px';
    noz.node.style.top = n.y + 'px';
    // Billboard the head so it always stands upright against the tilted bed.
    noz.node.style.transform = 'rotateZ(' + -cam.yaw + 'deg) rotateX(' + -cam.tilt + 'deg)';

    var lift = n.lift;
    noz.tip.style.bottom = lift + 'px';
    noz.block.style.bottom = lift + 22 + 'px';
    noz.body.style.bottom = lift + 50 + 'px';
    noz.rib1.style.bottom = lift + 66 + 'px';
    noz.rib2.style.bottom = lift + 96 + 'px';
    noz.dot.style.bottom = lift + 1 + 'px';

    var a = n.extruding ? '55' : '22';
    if (noz._a !== a) {
      noz._a = a;
      noz.glow.style.background = 'radial-gradient(closest-side,' + this.opts.hot + a + ',transparent)';
    }

    if (this.rail) {
      this.rail.style.opacity = cam.grid * 0.9;
      this.rail.style.top = n.y - 0.5 + 'px';
    }
  };

  SFPrintReveal.prototype._renderHud = function (opacity, t) {
    if (!this.hud) return;
    if (opacity <= 0.002) {
      this.hud.node.style.opacity = '0';
      return;
    }
    this.hud.node.style.opacity = opacity;
    var layer = 0;
    for (var i = 0; i < SEQ.items.length; i++) if (t > SEQ.items[i].tv) layer = SEQ.items[i].n;
    var pct = Math.round(clamp01(t / SEQ.total) * 100);
    var label = 'LAYER ' + String(layer).padStart(2, '0') + ' / ' + SEQ.items.length;
    if (this.hud._label !== label) { this.hud._label = label; this.hud.layer.textContent = label; }
    if (this.hud._pct !== pct) {
      this.hud._pct = pct;
      this.hud.pct.textContent = String(pct).padStart(3, '0') + '%';
      this.hud.fill.style.width = pct + '%';
    }
  };

  /* Render the whole rig at an absolute time on the timeline. */
  SFPrintReveal.prototype._renderAt = function (clock) {
    var acc = 0, i = 0, scene = SCENES[0], p = 0;
    for (; i < SCENES.length; i++) {
      if (clock < acc + SCENES[i].dur || i === SCENES.length - 1) {
        scene = SCENES[i];
        p = clamp01((clock - acc) / SCENES[i].dur);
        break;
      }
      acc += SCENES[i].dur;
    }

    var cam, t, nozzle = null, bloom = 0, wash = 0, hudT, hudOpacity;

    if (scene.name === 'bed') {
      cam = mixCam(CAM.c0, CAM.c1, MOTION.ease(p));
      cam.grid = CAM.c1.grid * MOTION.out(clamp01(p / 0.45));
      cam.hud = MOTION.out(clamp01((p - 0.35) / 0.4));
      var drop = MOTION.ease(clamp01((p - 0.18) / 0.6));
      t = -1;
      nozzle = { x: HOME.x, y: HOME.y, lift: lerp(900, 0, drop), opacity: clamp01((p - 0.1) / 0.2), extruding: false };
      hudT = 0;
      hudOpacity = cam.hud;
    } else if (scene.name === 'print') {
      cam = mixCam(CAM.c1, CAM.c2, MOTION.ease(p));
      t = MOTION.lin(p) * SEQ.total;
      var head = { x: HOME.x, y: HOME.y, extruding: false };
      for (var k = 0; k < SEQ.items.length; k++) {
        var it = SEQ.items[k];
        if (t >= it.tv && t < it.t0) {
          var tf = (t - it.tv) / Math.max(it.t0 - it.tv, 1e-4);
          head = { x: lerp(head.x, it.sx, tf), y: lerp(head.y, it.y, tf), extruding: false };
        } else if (t >= it.t0) {
          head = {
            x: lerp(it.sx, it.ex, clamp01((t - it.t0) / (it.t1 - it.t0))),
            y: it.y,
            extruding: t <= it.t1
          };
        }
      }
      nozzle = { x: head.x, y: head.y, lift: head.extruding ? 0 : 10, opacity: 1, extruding: head.extruding };
      hudT = t;
      hudOpacity = cam.hud;
    } else if (scene.name === 'settle') {
      cam = mixCam(CAM.c2, CAM.c3, MOTION.accel(p));
      bloom = MOTION.accel(p) * 0.35;
      t = SEQ.total + p * 3;
      var last = SEQ.items[SEQ.items.length - 1];
      var up = MOTION.out(clamp01((p - 0.05) / 0.5));
      nozzle = {
        x: last.ex, y: last.y, lift: up * 700,
        opacity: 1 - MOTION.ease(clamp01((p - 0.3) / 0.45)), extruding: false
      };
      hudT = SEQ.total;
      hudOpacity = cam.hud;
    } else {
      var pp = MOTION.decel(p);
      cam = mixCam(CAM.c3, CAM.c4, pp);
      bloom = 0.35 + MOTION.decel(clamp01((p - 0.1) / 0.75)) * 0.65;
      var flash = Math.max(0, Math.sin(clamp01((p - 0.34) / 0.26) * Math.PI)) * 0.5;
      wash = 0.03 * bloom + flash * 0.035;
      t = 999;
      hudT = SEQ.total;
      hudOpacity = cam.hud;
    }

    this._applyCam(cam);
    this._renderPart(t, bloom);
    this._renderNozzle(nozzle, cam);
    this._renderHud(hudOpacity, hudT);
    this.wash.style.opacity = wash > 0 ? 1 : 0;
    if (wash > 0) {
      this.wash.style.background =
        'radial-gradient(ellipse 46% 40% at 50% 50%,rgba(255,255,255,' + wash + '),transparent 70%)';
    }
    if (this.opts.onFrame) this.opts.onFrame(clock / TOTAL, scene.name);
  };

  /* ---------- transport ---------- */

  SFPrintReveal.prototype.seek = function (progress) {
    if (this._destroyed) return;
    this._clock = clamp01(progress) * TOTAL;
    this._renderAt(this._clock);
  };

  SFPrintReveal.prototype.play = function (fromStart) {
    if (this._destroyed || this._playing) return;
    if (fromStart !== false) this._clock = 0;
    this._playing = true;
    this._last = 0;
    this.root.classList.add('sfpr--playing');
    var self = this;
    var tick = function (now) {
      if (!self._playing) return;
      if (!self._last) self._last = now;
      // Cap the step so a backgrounded tab doesn't jump the whole timeline.
      var dt = Math.min((now - self._last) / 1000, 0.05) * self.opts.speed;
      self._last = now;
      self._clock += dt;
      if (self._clock >= TOTAL) {
        self._clock = TOTAL;
        self._renderAt(self._clock);
        self._finish();
        return;
      }
      self._renderAt(self._clock);
      self._raf = window.requestAnimationFrame(tick);
    };
    this._raf = window.requestAnimationFrame(tick);
  };

  SFPrintReveal.prototype.pause = function () {
    this._playing = false;
    if (this._raf) window.cancelAnimationFrame(this._raf);
    this._raf = 0;
  };

  SFPrintReveal.prototype._finish = function () {
    this._playing = false;
    this._raf = 0;
    this.root.classList.remove('sfpr--playing');
    this.root.classList.add('sfpr--done');
    if (this.opts.onEnd) this.opts.onEnd();
    if (this.opts.mode === 'loop' && !this._destroyed) {
      var self = this;
      this._loopTimer = window.setTimeout(function () {
        if (self._destroyed) return;
        self.root.classList.remove('sfpr--done');
        self.play(true);
      }, Math.max(0, this.opts.loopDelay) * 1000);
    }
  };

  /* ---------- drivers ---------- */

  SFPrintReveal.prototype._initAutoplay = function () {
    this.seek(0);
    var self = this;
    if (!('IntersectionObserver' in window)) { this.play(); return; }
    this._io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        self._io.disconnect();
        self._io = null;
        self.play();
      });
    }, { threshold: 0.35 });
    this._io.observe(this.root);
  };

  SFPrintReveal.prototype._initScrub = function () {
    var self = this;
    this.seek(0);
    var pending = false;
    this._onScroll = function () {
      if (pending) return;
      pending = true;
      window.requestAnimationFrame(function () {
        pending = false;
        if (self._destroyed) return;
        var r = self.root.getBoundingClientRect();
        var vh = window.innerHeight || document.documentElement.clientHeight;
        // 0 when the element's top hits the bottom of the viewport,
        // 1 when its bottom clears the top.
        var span = r.height + vh;
        self.seek(span <= 0 ? 0 : (vh - r.top) / span);
      });
    };
    window.addEventListener('scroll', this._onScroll, { passive: true });
    this._onScroll();
  };

  SFPrintReveal.prototype.destroy = function () {
    this._destroyed = true;
    this.pause();
    if (this._loopTimer) window.clearTimeout(this._loopTimer);
    if (this._io) { this._io.disconnect(); this._io = null; }
    if (this._onScroll) window.removeEventListener('scroll', this._onScroll);
    window.removeEventListener('resize', this._onResize);
  };

  /* Ink box of the emblem in part space — the printed bars, not the 1000x907
     bed they sit on. The logo handoff has to match ink to ink. */
  var INK = (function () {
    var l = Infinity, r = -Infinity, t = Infinity, b = -Infinity;
    BARS.forEach(function (x) {
      if (x.L < l) l = x.L;
      if (x.R > r) r = x.R;
      if (x.y < t) t = x.y;
      if (x.y > b) b = x.y;
    });
    return { w: r - l, h: b - t + BAR_H, cx: (l + r) / 2, cy: (t + b) / 2 };
  })();

  /* Screen-space geometry of the finished part, plus the transform-origin
     (local to the container) that scales about its centre.

     The returned frame is the FULL 1000x907 bed, not the printed beads. That is
     deliberate: the emblem geometry was measured inside the logo asset's own
     framing, so the bed maps 1:1 onto the logo's box and the beads land exactly
     where the logo's ink is. Measured against Logo_Emblem_No_BG.png — rig ink
     spans x 0.1699-0.8183, y 0.1907-0.8149 of the bed; the asset's ink spans
     x 0.1676-0.8171, y 0.1891-0.8151 of the file. Handing off ink-to-box
     instead would oversize the emblem by ~1.54x.

     `.ink` carries the beads' own bounds for measurement and debugging. */
  SFPrintReveal.prototype.partRect = function () {
    var cam = this._lastCam || CAM.c4;
    var box = this.root.getBoundingClientRect();
    var s = cam.s * this._fit;
    var fit = this._fit;

    function frame(w, h, cx, cy) {
      var ox = box.width / 2 + (cx - PW / 2) * s;
      var oy = box.height / 2 + cam.ty * fit + (cy - PH / 2) * s;
      return {
        width: w * s, height: h * s,
        cx: box.left + ox, cy: box.top + oy,
        originX: ox, originY: oy
      };
    }

    var bed = frame(PW, PH, PW / 2, PH / 2);
    bed.ink = frame(INK.w, INK.h, INK.cx, INK.cy);
    return bed;
  };

  SFPrintReveal.TOTAL = TOTAL;
  window.SFPrintReveal = SFPrintReveal;

  /* ---------- declarative boot: any [data-sf-print-reveal] element ---------- */
  function boot(scope) {
    (scope || document).querySelectorAll('[data-sf-print-reveal]:not([data-sfpr-ready])').forEach(function (node) {
      node.setAttribute('data-sfpr-ready', '');
      var cfg = {};
      try { cfg = JSON.parse(node.getAttribute('data-sf-print-reveal') || '{}'); } catch (e) { cfg = {}; }
      node.sfPrintReveal = new SFPrintReveal(node, cfg);
      node.dispatchEvent(new CustomEvent('sfpr:ready', { bubbles: true }));
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(document); });
  } else {
    boot(document);
  }

  // Theme editor re-renders sections in place.
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    e.target.querySelectorAll('[data-sfpr-ready]').forEach(function (n) {
      if (n.sfPrintReveal) n.sfPrintReveal.destroy();
    });
  });

  window.SFPrintRevealBoot = boot;
})();
